import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

interface ErrorFallbackProps {
    onReset: () => void;
    onReload: () => void;
}

/**
 * Fallback UI rendered by ErrorBoundary when a downstream React tree
 * throws. Mirrors the ai-visualizer "oops" page (centered icon +
 * heading + description + Try-again / Reload-page buttons), adapted
 * to the design tokens so it respects dark mode and i18n.
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ onReset, onReload }) => {
    const { t } = useTranslation();

    return (
        <div className="flex min-h-screen w-screen flex-col items-center justify-center gap-4 bg-muted p-6 text-center">
            <span className="mb-1 flex size-14 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="size-8 text-destructive" strokeWidth={1.6} />
            </span>

            <h1 className="font-heading text-xl font-medium tracking-tight">
                {t('ERROR_BOUNDARY.TITLE')}
            </h1>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
                {t('ERROR_BOUNDARY.DESCRIPTION')}
            </p>

            <div className="mt-4 flex gap-3">
                <Button onClick={onReset}>{t('ERROR_BOUNDARY.TRY_AGAIN')}</Button>
                <Button variant="outline" onClick={onReload}>
                    {t('ERROR_BOUNDARY.RELOAD')}
                </Button>
            </div>
        </div>
    );
};
